import React, { useState } from "react";
import PageTitle from "components/PageTitle/PageTitle";
import { Button, CircularProgress, Grid } from "@material-ui/core";
import { Typography } from "components/Wrappers/Wrappers";

import Field from "components/CustomFields/Field.js";
import SelectField from "components/CustomFields/SelectField.js";
import DistributorAuthoritarianField from "components/CustomFields/DistributorAuthoritarianField";
import CustomizedSnackbars from "components/CustomizedSnackbars/CustomizedSnackbars";
import LoaderBar from "components/LoaderBar/LoaderBar";

import { dbGet, dbPut } from "utils/DBFetchers.js";
import { useHistory } from "react-router-dom";
import routes from "routes";

// styles
import useStyles from "./styles";
import { SnackbarHandler } from "components/Handlers/SnackbarHandler";

const blankEditDistributorForm = {
  id: "",
  name: "",
  programs: "",
  authoritarian: null,
};

export default function ABMDistributorsEditPage(props) {
  var classes = useStyles();
  var [isLoading, setIsLoading] = useState(false);
  const [loadStatus, setLoadStatus] = useState("loading");
  const history = useHistory();
  const [editDistributorForm, updateForm] = useState(blankEditDistributorForm);
  const [allDistributors, setAllDistributors] = useState([]);
  const [isNameValid, setIsNameValid] = useState({ name: false, message: "" });

  const [snackbarStatus, snackbarOpen, onCloseSnackbar] = SnackbarHandler();

  const handleChange = (event) => {
    updateForm({
      ...editDistributorForm,
      [event.target.name]: event.target.value,
    });
  };

  const handleNameVerification = () => {
    var nameExists = allDistributors.some(
      (elem) => elem.name === editDistributorForm.name,
    );
    if (nameExists)
      setIsNameValid({
        name: nameExists,
        message: "Distribuidor ya existente.",
      });
    else setIsNameValid({ name: nameExists, message: "" });
  };

  React.useEffect(() => {
    if (!editDistributorForm.name) return; // no fetch on page load
    handleNameVerification();
  }, [editDistributorForm.name]);

  /* Fills distributor name options */
  const getDistributors = (distributorId) => {
    return dbGet("distributors").then((data) => {
      let mapped = data.map((item) => {
        return {
          id: item["id"],
          name: item["name"],
        };
      });
      setAllDistributors(mapped.filter((x) => x.id !== distributorId));
    });
  };

  /* Fills form data */
  const getDistributorInfo = (distributorId) => {
    return dbGet(`distributors/${distributorId}`).then((data) => {
      updateForm({
        ...editDistributorForm,
        id: distributorId,
        name: data.name ? data.name : "",
        programs: (data.programs || [])
          .map((program) => program.name)
          .join(" | "),
        authoritarian: data.is_authoritarian ? 1 : 2,
      });
    });
  };

  const loadDistributor = (distributorId) => {
    setLoadStatus("loading");
    Promise.all([
      getDistributors(distributorId),
      getDistributorInfo(distributorId),
    ])
      .then(() => setLoadStatus("loaded"))
      .catch(() => {
        setLoadStatus("error");
        snackbarOpen("error", "Error al cargar los datos del distribuidor");
      });
  };

  /* Basic form check */
  const checkFormComplete = () => {
    if (!editDistributorForm.name || !editDistributorForm.authoritarian)
      return false;
    return true;
  };

  /* Submit handling */
  const handleSubmit = () => {
    if (!checkFormComplete()) {
      snackbarOpen(
        "error",
        "Hay campos sin completar o incorrectos. Por favor completelos para poder editar el distribuidor.",
      );
      return;
    }
    setIsLoading(true);

    // Give adecuate format to send form to db
    let completedForm = {
      name: editDistributorForm.name,
      is_authoritarian: editDistributorForm.authoritarian === 1 ? true : false,
    };

    // Sends to db
    dbPut(`distributors/${editDistributorForm.id}`, completedForm)
      .then((data) => {
        snackbarOpen("success", "Distribuidor editado exitosamente");
        redirectToDistributorsPage();
      })
      .catch((error) => {
        snackbarOpen(
          "error",
          error[0] ? error[0].description_es : "Error al editar distribuidor",
        ); // translate pending
        setIsLoading(false);
        return;
      });
  };

  /* Cancel editing distributor and go one page backwards */
  const cancelAndGoBack = () => {
    updateForm(blankEditDistributorForm);
    history.goBack();
  };

  /* Redirect to distributor table page after editing correctly */
  const redirectToDistributorsPage = (user) => {
    setTimeout(() => {
      updateForm(blankEditDistributorForm);
      setIsLoading(false);
      history.push(routes.abmDistributors);
    }, 2000);
  };

  /* On page load, get all data */
  React.useEffect(() => {
    if (props.props.location.state?.distributorId) {
      loadDistributor(props.props.location.state.distributorId);
    } else {
      history.push(routes.abmDistributors);
    }
  }, []);

  const title = `Editar Distribuidor${editDistributorForm.name ? ": " : ""}${
    editDistributorForm.name ? editDistributorForm.name : ""
  }`;

  return (
    <>
      <div>
        <PageTitle title={title} />
      </div>
      <CustomizedSnackbars
        severity={snackbarStatus.severity}
        mssg={snackbarStatus.message}
        open={snackbarStatus.open}
        onClose={onCloseSnackbar}
      />
      {loadStatus === "loading" ? (
        <LoaderBar />
      ) : loadStatus === "error" ? (
        <Button
          variant="contained"
          color="primary"
          onClick={() =>
            loadDistributor(props.props.location.state.distributorId)
          }
        >
          Reintentar
        </Button>
      ) : (
        <Grid
          style={{ display: "flex", margin: 0 }}
          container
          direction="row"
          justify="flex-start"
          alignItems="center"
          spacing={4}
        >
          <form autoComplete="off">
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h5" className={classes.text}>
                  Datos del distribuidor
                </Typography>
                <div
                  className={classes.text}
                  style={{
                    marginTop: "1%",
                    display: "flex",
                    justifyContent: "space-between",
                    width: 700,
                  }}
                >
                  <Typography weight="medium">
                    A continuación edite los datos del distribuidor.
                  </Typography>
                </div>
                <div style={{ marginTop: "4%" }}>
                  <Field
                    labelName="Nombre *"
                    required={true}
                    id="name"
                    value={editDistributorForm.name}
                    divClassType={classes.generalPurposeItems}
                    fieldClassType={classes.generalPurposeField}
                    onChange={handleChange}
                    error={isNameValid.name}
                    helperText={isNameValid.message}
                  ></Field>
                  <SelectField
                    divClassName={classes.generalPurposeItems}
                    fieldClassName={classes.generalPurposeField}
                    displayText="Programa/s"
                    id="programs"
                    label="Programa/s asociado/s"
                    name="programs"
                    value={editDistributorForm.programs}
                    disabled={true}
                    allValues={
                      editDistributorForm.programs
                        ? [
                            {
                              id: editDistributorForm.programs,
                              name: editDistributorForm.programs,
                            },
                          ]
                        : []
                    }
                  />
                  <DistributorAuthoritarianField
                    divClassName={classes.generalPurposeItems}
                    fieldClassName={classes.generalPurposeField}
                    value={editDistributorForm.authoritarian}
                    onChange={handleChange}
                  />
                </div>
              </Grid>
            </Grid>

            <Grid container spacing={10}>
              <Grid item xs={12}>
                <div className={classes.buttonSpacing}>
                  {isLoading ? (
                    <CircularProgress
                      size={26}
                      className={classes.loginLoader}
                    />
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      onClick={handleSubmit}
                    >
                      Guardar
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={cancelAndGoBack}
                  >
                    Cancelar
                  </Button>
                </div>
              </Grid>
            </Grid>
          </form>
        </Grid>
      )}
    </>
  );
}
